import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse
} from '@angular/common/http';
import { Observable, from } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { IdempotencyService } from '../idempotency.service';
import { LocalStorageService } from 'ngx-webstorage';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { STORAGE_USER_NAME } from '../../shared/constants';
import { USE_UUID_IDEMPOTENCY_KEY } from './idempotency-context';

@Injectable()
export class IdempotencyInterceptor implements HttpInterceptor {
  private readonly IDEMPOTENCY_HEADER = 'x-idempotency-key';
  private readonly IDEMPOTENCY_REPLAY_HEADER = 'x-idempotency-replay';
  private readonly FORCE_UUID_HEADER = 'x-force-uuid-idempotency';
  private readonly METHODS_TO_INCLUDE = ['POST', 'PUT', 'PATCH', 'DELETE'];
  private readonly METHODS_TO_EXCLUDE = ['GET', 'HEAD', 'OPTIONS', 'TRACE'];

  private idempotencyService: IdempotencyService;
  private localStorageService: LocalStorageService;
  private toastr: ToastrService;
  private translateService: TranslateService;

  constructor(private injector: Injector) {
    this.idempotencyService = this.injector.get(IdempotencyService);
    this.localStorageService = this.injector.get(LocalStorageService);
    this.toastr = this.injector.get(ToastrService);
    this.translateService = this.injector.get(TranslateService);
  }

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (req.headers.has(this.IDEMPOTENCY_HEADER)) {
      return next.handle(req);
    }

    if (this.METHODS_TO_EXCLUDE.includes(req.method.toUpperCase())) {
      return next.handle(req);
    }

    if (!this.METHODS_TO_INCLUDE.includes(req.method.toUpperCase())) {
      return next.handle(req);
    }

    if (!this.isAuthenticated()) {
      return next.handle(req);
    }

    if (this.isAuthEndpoint(req.url)) {
      return next.handle(req);
    }

    return from(this.generateIdempotencyKey(req)).pipe(
      switchMap(idempotencyKey => {
        const clonedReq = req.clone({
          setHeaders: {
            [this.IDEMPOTENCY_HEADER]: idempotencyKey
          }
        });
        return next.handle(clonedReq).pipe(
          tap(event => {
            if (event instanceof HttpResponse) {
              this.handleIdempotencyReplay(event);
            }
          })
        );
      })
    );
  }

  private handleIdempotencyReplay(response: HttpResponse<unknown>): void {
    const replayHeader = response.headers.get(this.IDEMPOTENCY_REPLAY_HEADER);

    if (replayHeader) {
      this.toastr.info(
        this.translateService.instant('errors.idempotency.replay'),
        this.translateService.instant('errors.idempotency.replay_title')
      );
    }
  }

  private isAuthenticated(): boolean {
    return document.cookie.includes('XSRF-TOKEN');
  }

  private isAuthEndpoint(url: string): boolean {
    return url.includes('/auth/login') ||
           url.includes('/auth/logout') ||
           url.includes('/auth/csrf-cookie');
  }

  private async generateIdempotencyKey(req: HttpRequest<unknown>): Promise<string> {
    // Check if UUID is forced via HttpContext
    const forceUuidViaContext = req.context.get(USE_UUID_IDEMPOTENCY_KEY);

    // Check if UUID is forced via custom header
    const forceUuidViaHeader = req.headers.has(this.FORCE_UUID_HEADER);

    if (forceUuidViaContext || forceUuidViaHeader) {
      return this.idempotencyService.generateUuidKey();
    }

    const userId = this.getUserId();

    if (this.idempotencyService.isJsonOrScalar(req.body)) {
      return this.idempotencyService.generateDeterministicKey(
        req.url,
        req.method,
        req.body,
        userId
      );
    } else {
      return this.idempotencyService.generateUuidKey();
    }
  }

  private getUserId(): string | undefined {
    try {
      const user = this.localStorageService.retrieve(STORAGE_USER_NAME) as { id: string } | null;
      return user?.id;
    } catch {
      return undefined;
    }
  }
}
